import React from 'react';
import { render, cleanup, act } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { enableMocking, disableMocking, MOCK_DB } from '../mockApi';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderWithMock(element, assertions, waitMs = 400) {
  enableMocking();
  let snapshot = null;
  const logCapture = createLogCapture();
  try {
    let result;
    await act(async () => { result = render(element); });
    await act(async () => { await new Promise(r => setTimeout(r, waitMs)); });
    await act(async () => { await new Promise(r => setTimeout(r, 100)); });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    disableMocking();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

async function renderComponent(element, assertions, waitMs = 200) {
  const logCapture = createLogCapture();
  let snapshot = null;
  try {
    let result;
    await act(async () => { result = render(element); });
    await act(async () => { await new Promise(r => setTimeout(r, waitMs)); });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

export const questionnaireTests = [

  // ─── QuestionnaireTab ────────────────────────────────────────────────────────

  {
    id: 'questionnaire-tab-renders',
    name: 'QuestionnaireTab: renders container after API loads',
    run: async () => {
      const QuestionnaireTab = require('../../Components/Questionnaire/QuestionnaireTab').default;
      return await renderWithMock(
        withWrapper(<QuestionnaireTab projectUuid="p-test-1" />, '/project/p-test-1/flow'),
        async ({ container }) => {
          const tab = container.querySelector('[data-testid="questionnaire-tab-container"]');
          assert(tab !== null, 'questionnaire-tab-container must render after data loads');
        }
      );
    },
  },

  {
    id: 'questionnaire-tab-create-btn',
    name: 'QuestionnaireTab: shows create button',
    run: async () => {
      const QuestionnaireTab = require('../../Components/Questionnaire/QuestionnaireTab').default;
      return await renderWithMock(
        withWrapper(<QuestionnaireTab projectUuid="p-test-1" />, '/project/p-test-1/flow'),
        async ({ container }) => {
          const btn = container.querySelector('[data-testid="questionnaire-tab-create-btn"]');
          assert(btn !== null, 'questionnaire-tab-create-btn must render');
        }
      );
    },
  },

  {
    id: 'questionnaire-tab-item-count',
    name: 'QuestionnaireTab: shows one item per questionnaire returned by API',
    run: async () => {
      const QuestionnaireTab = require('../../Components/Questionnaire/QuestionnaireTab').default;
      return await renderWithMock(
        withWrapper(<QuestionnaireTab projectUuid="p-test-1" />, '/project/p-test-1/flow'),
        async ({ container }) => {
          const items = container.querySelectorAll('[data-testid^="questionnaire-tab-item-"]');
          assert(
            items.length === MOCK_DB.questionnaires.length,
            `Expected ${MOCK_DB.questionnaires.length} questionnaire items, found ${items.length}`
          );
        }
      );
    },
  },

  {
    id: 'questionnaire-tab-item-titles',
    name: 'QuestionnaireTab: each item shows the questionnaire title',
    run: async () => {
      const QuestionnaireTab = require('../../Components/Questionnaire/QuestionnaireTab').default;
      return await renderWithMock(
        withWrapper(<QuestionnaireTab projectUuid="p-test-1" />, '/project/p-test-1/flow'),
        async ({ container }) => {
          MOCK_DB.questionnaires.forEach(q => {
            const item = container.querySelector(`[data-testid="questionnaire-tab-item-${q.uuid}"]`);
            assert(item !== null, `Item for questionnaire "${q.title}" (${q.uuid}) must exist`);
            assert(item.textContent.includes(q.title), `Item must show questionnaire title "${q.title}"`);
          });
        }
      );
    },
  },

  {
    id: 'questionnaire-tab-empty-state',
    name: 'QuestionnaireTab: shows empty state when API returns no questionnaires',
    run: async () => {
      enableMocking();
      const { default: QuestionnaireTab } = require('../../Components/Questionnaire/QuestionnaireTab');
      disableMocking();

      const axios = require('axios');
      const originalAdapter = axios.defaults.adapter;
      axios.defaults.adapter = (config) => new Promise(resolve => setTimeout(() => resolve({
        data: { data: [] },
        status: 200, statusText: 'OK',
        headers: { 'content-type': 'application/json' },
        config, request: {},
      }), 30));

      let snapshot = null;
      const logCapture = createLogCapture();
      try {
        let result;
        await act(async () => { result = render(withWrapper(<QuestionnaireTab projectUuid="p-test-1" />, '/project/p-test-1/flow')); });
        await act(async () => { await new Promise(r => setTimeout(r, 400)); });
        await act(async () => { await new Promise(r => setTimeout(r, 100)); });
        const emptyState = result.container.querySelector('[data-testid="questionnaire-tab-empty"]');
        assert(emptyState !== null, 'questionnaire-tab-empty must render when no questionnaires');
        const createBtn = result.container.querySelector('[data-testid="questionnaire-tab-empty-create-btn"]');
        assert(createBtn !== null, 'questionnaire-tab-empty-create-btn must exist in empty state');
        snapshot = captureComponentSnapshot(result.container);
      } finally {
        cleanup();
        axios.defaults.adapter = originalAdapter;
        logCapture.restore();
      }
      return { snapshot, logs: logCapture.getLogs() };
    },
  },

  // ─── QuestionnaireAnalyticsCard ───────────────────────────────────────────────

  {
    id: 'questionnaire-analytics-card-active',
    name: 'QuestionnaireAnalyticsCard: renders with active questionnaire data',
    run: async () => {
      const QuestionnaireAnalyticsCard = require('../../Components/Questionnaire/QuestionnaireAnalyticsCard').default;
      const activeQuestionnaire = MOCK_DB.questionnaires.find(q => q.status === 'active');
      return await renderComponent(
        withWrapper(
          <QuestionnaireAnalyticsCard
            questionnaire={activeQuestionnaire}
            projectUuid="p-test-1"
          />,
          '/project/p-test-1/flow'
        ),
        async ({ container }) => {
          assert(container.firstChild !== null, 'QuestionnaireAnalyticsCard must render');
          assert(
            container.textContent.includes(activeQuestionnaire.title),
            `Analytics card must show questionnaire title "${activeQuestionnaire.title}"`
          );
          assert(
            container.textContent.includes(String(activeQuestionnaire.total_responses)),
            'Analytics card must show total responses count'
          );
        }
      );
    },
  },

  {
    id: 'questionnaire-analytics-card-draft',
    name: 'QuestionnaireAnalyticsCard: renders with draft questionnaire data',
    run: async () => {
      const QuestionnaireAnalyticsCard = require('../../Components/Questionnaire/QuestionnaireAnalyticsCard').default;
      const draftQuestionnaire = MOCK_DB.questionnaires.find(q => q.status === 'draft');
      return await renderComponent(
        withWrapper(
          <QuestionnaireAnalyticsCard
            questionnaire={draftQuestionnaire}
            projectUuid="p-test-1"
          />,
          '/project/p-test-1/flow'
        ),
        async ({ container }) => {
          assert(container.firstChild !== null, 'QuestionnaireAnalyticsCard must render for draft questionnaire');
          assert(
            container.textContent.includes(draftQuestionnaire.title),
            `Analytics card must show questionnaire title "${draftQuestionnaire.title}"`
          );
        }
      );
    },
  },

  {
    id: 'questionnaire-analytics-card-questions-count',
    name: 'QuestionnaireAnalyticsCard: shows questions count and completion rate',
    run: async () => {
      const QuestionnaireAnalyticsCard = require('../../Components/Questionnaire/QuestionnaireAnalyticsCard').default;
      const questionnaire = MOCK_DB.questionnaires.find(q => q.questions_count > 0 && q.completion_rate > 0);
      return await renderComponent(
        withWrapper(
          <QuestionnaireAnalyticsCard
            questionnaire={questionnaire}
            projectUuid="p-test-1"
          />,
          '/project/p-test-1/flow'
        ),
        async ({ container }) => {
          assert(
            container.textContent.includes(String(questionnaire.questions_count)),
            `Analytics card must show questions count "${questionnaire.questions_count}"`
          );
          assert(
            container.textContent.includes(String(Math.round(questionnaire.completion_rate))),
            `Analytics card must show completion rate "${questionnaire.completion_rate}"`
          );
        }
      );
    },
  },

];
